import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Plus, Search, Settings } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";
import { useDebounce } from "use-debounce";

import { menusSearchElements } from "~/api-refs";
import { toConvexTableId } from "~/lib/convex-table-id";

import { Button } from "../ui/button";
import { Combobox } from "../ui/combobox";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../ui/input-group";

interface MenuPageItem {
  id: string;
  name: string;
  [key: string]: unknown;
}

export const MenuSelector = ({ menus }: { menus: MenuPageItem[] }) => {
  const navigate = useNavigate();
  const params = useParams({ strict: false });

  return (
    <div className="flex items-center gap-3 pb-6" id="menu-selector">
      <div className="w-full sm:w-auto sm:min-w-72" id="menu-combobox">
        <Combobox
          className="h4 font-heading h-13 rounded-md"
          disabled={!menus?.length}
          dropdownClassName="w-60"
          filter={(_value: string, search: string, keywords?: string[]) => {
            if (!search) {
              return 1;
            }
            if (!keywords?.[0]) {
              return 0;
            }
            const lowerCasedQuery = search.toLowerCase();
            if (keywords[0] === lowerCasedQuery) {
              return 3;
            }
            if (keywords[0].startsWith(lowerCasedQuery)) {
              return 2;
            }
            if (keywords[0].includes(lowerCasedQuery)) {
              return 1;
            }
            return 0;
          }}
          items={
            menus?.length
              ? menus.map((menu: NonNullable<typeof menus>[number]) => ({
                  label: menu.name,
                  value: menu.id,
                  keywords: [menu.name.toLowerCase()],
                }))
              : []
          }
          onValueChange={(value: string | null) => {
            if (!value) {
              return;
            }
            void navigate({ to: `/menus/${value}/categories` });
          }}
          value={params.menuId ?? ""}
        />
      </div>
      {/* V4.1: Route-based edit for URL context tracking */}
      <Button
        className="text-muted-foreground hover:text-foreground shrink-0 hover:bg-transparent"
        onClick={() => navigate({ to: `/menus/${params.menuId}/edit` })}
        size="icon"
        type="button"
        variant="ghost"
      >
        <Settings className="size-5" />
        <span className="sr-only">Edit this menu</span>
      </Button>
      {/* V4.1: Route-based create for URL context tracking */}
      <Button
        className="shrink-0"
        onClick={() => navigate({ to: "/menus/new" })}
        size="lg"
        type="button"
        variant="default"
      >
        <Plus className="size-4" />
        Create New Menu
      </Button>
      <div className="flex-1" />
      <SearchBar menuId={params.menuId ?? ""} menus={menus} />
    </div>
  );
};

const SearchBar = ({
  menuId,
  menus,
}: {
  menuId: string;
  menus: MenuPageItem[];
}) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const id = useId();
  const [hoveredIndex, setHoveredIndex] = useState(0);
  const navigate = useNavigate();
  const [popupSection, setPopupSection] = useState<
    "results" | "createNew" | null
  >(null);
  const isListboxHovered = useRef(false);

  // Check if the current menu exists in the menus list
  const menuExists = menus?.some(
    (menu: NonNullable<typeof menus>[number]) => menu.id === menuId
  );

  const { data, isPending: isLoading } = useQuery({
    ...convexQuery(
      menusSearchElements,
      debouncedQuery && menuExists
        ? {
            menuId: toConvexTableId("menus", menuId),
            query: debouncedQuery,
          }
        : "skip"
    ),
  });

  const navigateToUrl = (url: string) => {
    setPopupSection(null);
    setQuery("");
    void navigate({ to: url });
  };

  const createNewMenuElement = (element: string) => {
    setPopupSection(null);
    setQuery("");
    window.dispatchEvent(
      new CustomEvent("openCreateMenuElementModal", {
        detail: {
          element,
          name: query,
          type: "new",
        },
      })
    );
  };

  useEffect(() => {
    setHoveredIndex(0);
  }, []);

  const createNewActions = [
    () => createNewMenuElement("category"),
    () => createNewMenuElement("item"),
    () => createNewMenuElement("modifier"),
  ] as const;

  const actions =
    popupSection === "createNew"
      ? createNewActions
      : [
          ...(data ?? []).map((item: NonNullable<typeof data>[number]) => {
            return () => navigateToUrl(item.url);
          }),
          () => {
            setPopupSection("createNew");
          },
        ];

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter":
      case "Space":
        e.preventDefault();
        actions[hoveredIndex]?.();
        break;
      case "Escape":
        e.preventDefault();
        setPopupSection(popupSection === "createNew" ? "results" : null);
        break;
      case "ArrowDown":
        e.preventDefault();
        setHoveredIndex((i) => (i + 1) % actions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHoveredIndex((i) => (i - 1 < 0 ? actions.length - 1 : i - 1));
        break;
      case "Home":
        e.preventDefault();
        setHoveredIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHoveredIndex(actions.length - 1);
        break;
      default:
        // Let other keys pass through for normal typing
        break;
    }
  };

  return (
    <div className="relative w-full max-w-md min-w-80">
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <Search className="text-foreground size-5" />
        </InputGroupAddon>
        <InputGroupInput
          aria-activedescendant={
            popupSection !== null
              ? `${id}-${hoveredIndex.toString()}`
              : undefined
          }
          aria-autocomplete="list"
          aria-controls={popupSection !== null ? `${id}-listbox` : undefined}
          aria-expanded={popupSection !== null}
          autoCapitalize="off"
          autoComplete="off"
          onBlur={() => {
            if (isListboxHovered.current) {
              return;
            }
            setTimeout(() => {
              setPopupSection(null);
            }, 500);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setQuery(e.currentTarget.value);
            setPopupSection(
              e.currentTarget.value.length > 0 ? "results" : null
            );
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Search, edit or create..."
          role="combobox"
          value={query}
        />
      </InputGroup>
      {popupSection !== null && (
        <ul
          className="border-border bg-background shadow-box absolute inset-x-0 top-14 z-40 space-y-0.5 overflow-x-hidden rounded-md border p-2"
          id={`${id}-listbox`}
          onMouseEnter={() => {
            isListboxHovered.current = true;
          }}
          onMouseLeave={() => {
            isListboxHovered.current = false;
          }}
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <ul> with role="listbox" is correct ARIA pattern for combobox listbox items per WAI-ARIA
          role="listbox"
        >
          {!data && isLoading && (
            <p className="body-sm text-foreground p-2 text-center">
              Loading...
            </p>
          )}
          {popupSection === "results" && (
            <>
              {data?.map(
                (item: NonNullable<typeof data>[number], i: number) => (
                  <SearchOption
                    hoveredIndex={hoveredIndex}
                    id={id}
                    index={i}
                    key={item.url}
                    label={item.label}
                    onClick={() => navigateToUrl(item.url)}
                    setHoveredIndex={setHoveredIndex}
                    subLabel={item.subLabel}
                  />
                )
              )}
              {data && data.length > 0 && (
                <div className="-mx-2 py-1.5">
                  <hr className="bg-muted block h-px" />
                </div>
              )}
              {data ? (
                <SearchOption
                  hoveredIndex={hoveredIndex}
                  id={id}
                  index={data.length}
                  label={`Create new: "${query}"`}
                  onClick={() => setPopupSection("createNew")}
                  setHoveredIndex={setHoveredIndex}
                />
              ) : null}
            </>
          )}
          {popupSection === "createNew" && (
            <>
              <p className="subtitle-sm text-foreground px-3 py-2">
                Create new...
              </p>
              <SearchOption
                hoveredIndex={hoveredIndex}
                id={id}
                index={0}
                label={`Category: "${query}"`}
                onClick={() => createNewActions[0]()}
                setHoveredIndex={setHoveredIndex}
              />
              <SearchOption
                hoveredIndex={hoveredIndex}
                id={id}
                index={1}
                label={`Item: "${query}"`}
                onClick={() => createNewActions[1]()}
                setHoveredIndex={setHoveredIndex}
              />
              <SearchOption
                hoveredIndex={hoveredIndex}
                id={id}
                index={2}
                label={`Modifier group: "${query}"`}
                onClick={() => createNewActions[2]()}
                setHoveredIndex={setHoveredIndex}
              />
            </>
          )}
        </ul>
      )}
    </div>
  );
};

const SearchOption = ({
  index,
  label,
  subLabel,
  id,
  hoveredIndex,
  setHoveredIndex,
  onClick,
}: {
  index: number;
  label: string;
  subLabel?: string;
  id: string;
  hoveredIndex: number;
  setHoveredIndex: (index: number) => void;
  onClick: () => void;
}) => (
  <li
    aria-selected={index === hoveredIndex}
    className={twJoin(
      "body-sm flex cursor-pointer items-center gap-2 rounded-md px-3 py-2",
      index === hoveredIndex ? "bg-muted text-foreground" : "text-foreground"
    )}
    id={`${id}-${index.toString()}`}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    onMouseEnter={() => setHoveredIndex(index)}
    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li> with role="option" is correct ARIA pattern for listbox items per WAI-ARIA
    role="option"
    tabIndex={-1}
  >
    {label}
    {subLabel && (
      <span className="body-sm text-muted-foreground">{subLabel}</span>
    )}
  </li>
);
